import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { EyeSlash } from "@styled-icons/fa-regular";
import { isAndroid } from "react-device-detect";
import { postPageRendered, postReady, send } from "../utils/postmessage";
import { installExternalPhotoDrop } from "../utils/externalPhotoDrop";
import { measureCoachmarkTarget } from "../utils/coachmark";
import { measureEditables } from "../utils/editable";
import {
  installPhotoPointerDrag,
  syncPhotoPointerDrag,
} from "../utils/photoPointerDrag";
import params from "../utils/params";
import PageErrorBoundary from "../components/PageErrorBoundary";

import Templates from "../templates";

// 96 is used as Puppeteer is having a weird 0.75 scale issue. So 72dpi / 0.75 = 96
const Container = styled.div`
  ${(props) =>
    props.view === "print" &&
    `
    @media print {
      page-break-after: always;
    }
    @page {
      size: ${(305 / 25.4) * 96}px ${(305 / 25.4) * 96}px;
    }
    * {
      -webkit-print-color-adjust: exact;
    }
  `}
`;

const Hidden = styled.div`
  position: fixed;
  z-index: 1000;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  width: 100vw;
  height: 100vw;
  justify-content: center;
  align-items: center;
`;

// Host-bound reports (see utils/postmessage) that older app builds could save
// into a page as if they were questions. They carry no position, so the
// positional templates shift and crash on them.
const REPORT_TYPES = new Set([
  "pageRendered",
  "overflow",
  "overflowWarning",
  "imageQualityWarning",
  "imageLoadingError",
  "coachmark-rect"
]);

const withoutReports = (page) => {
  const items = page?.customQuestions?.items;
  if (!Array.isArray(items) || !items.some((item) => REPORT_TYPES.has(item?.type))) {
    return page;
  }
  return {
    ...page,
    customQuestions: {
      ...page.customQuestions,
      items: items.filter((item) => !REPORT_TYPES.has(item?.type))
    }
  };
};

const Page = ({ pageId: pageIdProps, pageIndex }) => {
  const userId = params?.user;
  const bookId = params?.book;
  const pageId = pageIdProps || params?.page;
  const view = params?.view;

  // Document-level pointer machinery for the cooperative photo drag —
  // interactive editor embeds only; print/review DOMs stay untouched.
  useEffect(() => {
    if (view === "preview") installPhotoPointerDrag();
  }, [view]);
  const [page, setPage] = useState();
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const renderedPageId = page?.id;

  useEffect(() => {
    if (view === "preview") {
      return installExternalPhotoDrop(send, () => renderedPageId);
    }
  }, [view, renderedPageId]);

  useEffect(() => {
    postReady();
  }, []);

  // Ack each rendered page payload on the next frame (layout painted; photos
  // may still stream in). The app matches the id to lift recycled-tile
  // covers and to ignore stale acks after a fast scroll.
  useEffect(() => {
    if (!page?.id) return;
    const frame = requestAnimationFrame(() => postPageRendered(page.id));
    return () => cancelAnimationFrame(frame);
  }, [page]);

  useEffect(() => {
    const receiver = isAndroid ? document : window;
    const messageHandler = async (event) => {
      // Native react-native-webview delivers synthetic events with an empty
      // origin; the web app delivers real postMessages from the embedding
      // parent frame (cross-origin, so origin is set — filter by source).
      const fromNative = !event.origin;
      const fromParentFrame =
        window.parent !== window && event.source === window.parent;
      if (!fromNative && !fromParentFrame) return;
      if (typeof event.data !== "string") return;
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (data && data.type === "coachmark-measure") {
        measureCoachmarkTarget(data.target);
        return;
      }
      if (data && data.type === "measure-editables") {
        measureEditables();
        return;
      }
      // Cooperative photo drag lifecycle (see utils/photoPointerDrag.jsx):
      // the host broadcasts start/end so every pane joins in / cleans up.
      if (data && data.type === "photo-drag-sync") {
        syncPhotoPointerDrag(!!data.active, data.fromQuestionId || null);
        return;
      }
      // Host-driven selection: {type: "select", questionId} highlights the
      // matching editable element; a null questionId clears the highlight.
      if (data && data.type === "select") {
        setSelectedQuestionId(data.questionId || null);
        return;
      }
      // Identify a page payload positively by its render fields. Control
      // messages (ready, overflow, coachmark-rect, ...) carry a `type` and
      // none of these. Filtering on `!data.type` was wrong: the authed
      // editor's payload is a CustomPage that legitimately includes a `type`
      // field, so real pages were being rejected and the preview stayed blank.
      const isPagePayload =
        data &&
        typeof data === "object" &&
        (data.template != null ||
          data.customQuestions != null ||
          data.questions != null);
      if (isPagePayload) {
        // A fresh page payload invalidates any host-driven selection.
        setSelectedQuestionId(null);
        setPage(withoutReports(data));
      }
    };

    receiver.addEventListener("message", messageHandler);

    return () => {
      receiver.removeEventListener("message", messageHandler);
    };
  }, []);

  // Applies the host-driven selection as a class on the matching editable
  // element. Done at the DOM level (instead of threading a prop through
  // every template) and re-run on page renders, which reset element
  // classNames, so the highlight is re-applied after each re-render.
  useEffect(() => {
    document.querySelectorAll(".editable-selected").forEach((el) => {
      el.classList.remove("editable-selected");
    });
    if (!selectedQuestionId) return;
    const escaped = window.CSS?.escape
      ? window.CSS.escape(selectedQuestionId)
      : selectedQuestionId;
    const el = document.querySelector(`[data-editable-id="${escaped}"]`);
    if (el) {
      el.classList.add("editable-selected");
    }
  }, [selectedQuestionId, page]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const getPage = await fetch(
          `${process.env.REACT_APP_PUBLIC_API_ENDPOINT}/user/${userId}/book/${bookId}/page/${pageId}`,
          {}
        );
        const json = await getPage.json();
        setPage(json);
      } catch (e) {
        // alert(e);
        console.log(e.message);
      }
    };

    if (bookId && pageId && userId && view === "print") {
      fetchPage();
    }
  }, [bookId, pageId, userId, view]);

  return (
    <Container view={view} data-page-index={pageIndex} data-page-number={page?.number}>
      {page && (
        <>
          {page?.hide && (
            <Hidden>
              <EyeSlash height="25%" color="white" />
            </Hidden>
          )}
          {view === "print" || view === "review" ? (
            // Print must fail loudly: a contained crash would ship a blank page.
            <Templates user={userId} page={page} />
          ) : (
            <PageErrorBoundary page={page}>
              <Templates user={userId} page={page} />
            </PageErrorBoundary>
          )}
        </>
      )}
    </Container>
  );
};

export default Page;
